import { DocumentManageDenial, DocumentManageResult, DocumentViewer, ManageableDocument } from '@pelicargo/types';

/**
 * How long an unattributed, unauthenticated upload stays claimable by an unauthenticated caller.
 *
 * The pre-login request flow has no session, so the *only* thing distinguishing its uploader from
 * a stranger is that it just happened. Ids are sequential, so without a bound anyone could walk
 * the id space deleting other people's in-flight uploads. Comfortably longer than filling in a
 * request takes, and shorter than the 2-day orphan cleanup, so nothing is stranded in between.
 */
export const ANONYMOUS_MANAGE_WINDOW_MS = 24 * 60 * 60 * 1000;

const deny = (reason: DocumentManageDenial, message: string): DocumentManageResult => ({
  ok: false,
  reason,
  message,
});

const OK: DocumentManageResult = { ok: true, reason: undefined, message: undefined };

/** Dates arrive as `Date` on the API and as ISO strings through tRPC (no superjson transformer). */
const toTime = (value: Date | string | null | undefined) => {
  if (value == null) return undefined;
  const time = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isNaN(time) ? undefined : time;
};

/**
 * Whether `viewer` may retype or delete this document, and if not, why.
 *
 * Reading and writing are different rights here. Every document on a request is visible to everyone
 * on it — the forwarder, the fulfillment partner and the tenant all see the same set, which is what
 * `_document.download` allows for. Managing is narrower, and deliberately so: neither side may touch
 * the other's paperwork.
 *
 * `viewer.accountId` is the account being *acted as*, which is client-supplied
 * (`x-target-account-id`). That is fine for this decision — it's the same value stamped onto
 * `account_id` at upload — but it means callers must not treat a pass here as proof of identity.
 *
 * Ownership is only half of it. Two integrity rules come first, and they bind the owner too:
 *
 * - **Approved documents are frozen.** Approval is the counterparty's sign-off on a specific file.
 *   Retyping or removing it afterwards leaves `approved_at` pointing at something that was never
 *   approved, and nothing in the update path clears that stamp. Staff can still intervene.
 * - **Provider documents belong to the integration.** `syncInlandDocuments` deletes and recreates
 *   them wholesale on every sync, so any user edit is silently discarded. Better to refuse than to
 *   accept a change that will disappear.
 *
 * Then ownership, in descending order of how well the claim can be established:
 *
 * - Attributed to an account → that account only.
 * - No account but on a request → nobody. These predate mandatory attribution or came in through a
 *   path that passed a nullable account; since ownership can't be established, no one may edit.
 * - No account, no request, but an author → that user. A signed-in upload made with no target
 *   account selected.
 * - None of the above → the pre-login flow. Claimable by anyone, but only briefly; see
 *   ANONYMOUS_MANAGE_WINDOW_MS.
 *
 * @param now - injectable for tests; defaults to the current time.
 */
export const checkManage = (
  document: Partial<ManageableDocument> | null | undefined,
  viewer: DocumentViewer = {},
  now: Date = new Date(),
): DocumentManageResult => {
  if (!document) return deny('not-persisted', 'Document not found.');

  // A row that doesn't exist yet — a placeholder standing in for an expected type — has nothing to
  // manage. Checked before the staff waiver: not even staff can edit a document that isn't there.
  if (document.id == null) return deny('not-persisted', 'That document has not been uploaded yet.');

  const isStaff = viewer.isStaff === true;

  if (!isStaff) {
    if (document.provider_id != null) {
      return deny(
        'provider-owned',
        'This document comes from the carrier integration and is replaced automatically. It cannot be edited here.',
      );
    }

    if (document.approved_at != null) {
      return deny(
        'approved',
        'This document has already been approved. Ask for the approval to be withdrawn before changing it.',
      );
    }
  }

  if (document.account_id != null) {
    if (isStaff) return OK;
    if (viewer.accountId == null) {
      return deny('not-owner', 'Only the account that uploaded a document can change or remove it.');
    }
    return document.account_id === viewer.accountId
      ? OK
      : deny('not-owner', 'Only the account that uploaded a document can change or remove it.');
  }

  if (document.request_id != null) {
    return isStaff ? OK : deny('unattributable', 'This document has no owner on record and cannot be edited.');
  }

  if (document.author_id != null) {
    if (isStaff) return OK;
    return viewer.userId != null && viewer.userId === document.author_id
      ? OK
      : deny('not-author', 'Only the person who uploaded this document can remove it.');
  }

  if (isStaff) return OK;

  // Anonymous pre-login upload. The client's payload may not carry `created_at`; when it doesn't we
  // can't evaluate freshness, so allow it and let the API — which always selects the column — be the
  // one that enforces. The UI is not a security boundary; being permissive here only risks offering
  // a delete button that the server then refuses.
  const createdAt = toTime(document.created_at);
  if (createdAt == null) return OK;

  return now.getTime() - createdAt <= ANONYMOUS_MANAGE_WINDOW_MS
    ? OK
    : deny('stale-anonymous', 'This upload can no longer be removed without signing in.');
};

/**
 * Boolean form of checkManage, for rendering decisions. Use the full result when you need to
 * tell the user *why* — the reasons are meaningfully different.
 */
export const canManage = (
  document: Partial<ManageableDocument> | null | undefined,
  viewer: DocumentViewer = {},
  now?: Date,
) => checkManage(document, viewer, now).ok === true;
